/**
 * DocumentViewer - Componente unificado para visualización de documentos
 *
 * FASE 1: Reemplaza 3 tabs separados (viewer-pdf, viewer-snippet, viewer-chunks)
 * con un solo viewer que adapta su contenido según el tipo.
 */

import React, { useMemo } from 'react';
import PDFViewerPanel from './PDFViewerPanel';
import SnippetViewerPanel from './SnippetViewerPanel';
import ChunksViewerPanel from './ChunksViewerPanel';
import MarkdownRenderer from './MarkdownRenderer';
import { useTheme } from '../contexts/ThemeContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { FileText, AlertCircle, X } from 'lucide-react';
import type { ViewerData } from '../types/viewer';
import type { PDFSnippet } from '../services/api';
import {
  createPDFViewerData,
  createSnippetViewerData,
  createChunkViewerData,
} from '../utils/viewerHelpers';
import { normalizeStudioResult, extractContentText } from '../utils/studioUtils';
import { parseStudioMarkdown } from '../utils/studioMarkdown';

// Type for Chunk (matching ChunksViewerPanel's expected type)
interface Chunk {
  id: string;
  text: string;
  page_number?: number;
  document_id?: string;
  document_name?: string;
  chunk_id?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Map ViewerData to PDFSnippet type
 */
const mapViewerDataToPDFSnippet = (viewerData: ViewerData): PDFSnippet | null => {
  if (viewerData.type !== 'snippet' || !viewerData.snippetId) {
    return null;
  }

  return {
    id: viewerData.snippetId,
    user_id: (viewerData.metadata?.user_id as string) || '',
    document_id: viewerData.documentId || '',
    title: viewerData.title || '',
    notes: viewerData.metadata?.notes as string | undefined,
    tags: (viewerData.metadata?.tags as string[]) || [],
    page_number: viewerData.pageNumber || 1,
    coordinates: viewerData.coordinates || { x: 0, y: 0, width: 0, height: 0 },
    snippet_type: viewerData.imageData ? 'image' : 'text',
    image_data: viewerData.imageData,
    text_content: viewerData.textContent,
    context_text: viewerData.metadata?.context_text as string | undefined,
    // Use actual document dates from backend, don't default to current date
    // If dates are not available in metadata, they should be fetched from the document
    created_at: (viewerData.metadata?.created_at as string) || '',
    updated_at: (viewerData.metadata?.updated_at as string) || '',
    document_name: viewerData.documentName,
    document_filename: viewerData.metadata?.document_filename as string | undefined,
    has_image: !!viewerData.imageData,
  };
};

/**
 * Map ViewerData to Chunk type
 */
const mapViewerDataToChunk = (viewerData: ViewerData): Chunk | null => {
  if (viewerData.type !== 'chunk' || !viewerData.chunkId) {
    return null;
  }

  return {
    id: viewerData.chunkId,
    text: viewerData.textContent || '',
    page_number: viewerData.pageNumber,
    document_id: viewerData.documentId,
    document_name: viewerData.documentName,
    chunk_id: viewerData.chunkId,
    metadata: viewerData.metadata,
  };
};

export interface DocumentViewerProps {
  viewerData: ViewerData | null;
  onClose: () => void;
  onCitationClick?: (citation: any) => void;
  onChunkClick?: (citation: any) => void;
  onSnippetSaved?: (snippetId: string) => void; // Callback cuando se guarda un snippet
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  viewerData,
  onClose,
  onCitationClick,
  onChunkClick,
  onSnippetSaved,
}) => {
  const { theme } = useTheme();

  // Cerrar visor con tecla Escape
  useEscapeKey(onClose, !!onClose);

  // Función para determinar el tipo de visor basado en viewerData
  const getViewerType = () => {
    // Icono común para todos los visores
    const icon = <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${theme.colors.text.primary}`} />;

    if (!viewerData || !viewerData.metadata) {
      return { title: 'Visor PDF', icon };
    }

    const metadata = viewerData.metadata;
    const type = metadata?.type as string | undefined;

    if (type === 'saved-message') {
      return { title: 'Visor Mensaje', icon };
    }

    if (type === 'snippet') {
      return { title: 'Visor Fragmento', icon };
    }

    if (type === 'studio-result') {
      return { title: 'Visor Studio', icon };
    }

    // Si no hay type específico, es un PDF
    return { title: 'Visor PDF', icon };
  };

  // Panel header removed — close via AppHeader "Volver"

  if (!viewerData) {
    return (
      <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
        <div
          className={`flex-1 flex items-center justify-center ${theme.colors.bg.primary} ${theme.colors.text.secondary}`}
        >
          <div className="text-center px-6">
            <FileText size={48} className="mb-4" />
            <p className="text-lg font-semibold">Selecciona un documento para visualizar</p>
            <p className="text-sm">
              Puedes abrir PDFs, fragmentos o chunks desde el Drive o el Chat.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Procesar Studio result si es necesario (igual que StudiosPanel)
  const studioResult = useMemo(() => {
    if (
      !viewerData ||
      viewerData.type !== 'studio-result' ||
      !viewerData.metadata ||
      viewerData.metadata.type !== 'studio-result'
    ) {
      return null;
    }

    // Extraer datos del metadata
    const metadata = viewerData.metadata;
    // Intentar obtener markdown de múltiples lugares
    const markdownString = viewerData.textContent || metadata.markdown || '';

    // También intentar obtener rawResult desde metadata si existe
    // (cuando se guarda, se guarda como { markdown: "...", raw: {...} })
    const rawResultFromMetadata = metadata.raw || null;

    if (!markdownString || markdownString.trim().length === 0) {
      return null;
    }

    // Parsear el markdown guardado
    const parsed = parseStudioMarkdown(markdownString);
    const frontMatter = parsed.data as Record<string, any>;

    // Procesar el contenido usando el mismo flujo que StudiosPanel
    // Intentar obtener result_data de múltiples lugares (prioridad):
    // 1. metadata.raw.assistant_message.attachments.studio_metadata.result_data (rawResult guardado - MEJOR OPCIÓN)
    // 2. metadata.normalized_result.result_data (si ya fue procesado previamente)
    // 3. frontMatter.result_data
    // 4. frontMatter.rawResult
    let resultDataToProcess = null;

    // Prioridad 1: Intentar desde metadata.raw (cuando se guardó el rawResult) - ESTA ES LA MEJOR OPCIÓN
    if (rawResultFromMetadata && typeof rawResultFromMetadata === 'object') {
      const raw = rawResultFromMetadata;
      // Verificar si raw ES directamente el result_data (tiene las claves esperadas de Studio)
      // Para Q&A: qa_pairs, titulo, resumen_contexto, insights_clave
      // Para Timeline: events, titulo, periodo_analizado
      // Para Summary: titulo, resumen_ejecutivo, puntos_clave
      const hasQAStructure = raw.qa_pairs && Array.isArray(raw.qa_pairs);
      const hasTimelineStructure = raw.events && Array.isArray(raw.events);
      const hasSummaryStructure = raw.resumen_ejecutivo || raw.puntos_clave;

      if (hasQAStructure || hasTimelineStructure || hasSummaryStructure) {
        resultDataToProcess = raw;
      }
      // Intentar múltiples rutas anidadas si no es directo
      else if (raw.assistant_message?.attachments?.studio_metadata?.result_data) {
        resultDataToProcess = raw.assistant_message.attachments.studio_metadata.result_data;
      } else if (raw.attachments?.studio_metadata?.result_data) {
        resultDataToProcess = raw.attachments.studio_metadata.result_data;
      } else if (raw.result_data) {
        resultDataToProcess = raw.result_data;
      }
    }

    // Prioridad 2: Si ya hay un normalized_result en metadata, usarlo directamente
    if (
      !resultDataToProcess &&
      metadata.normalized_result &&
      metadata.normalized_result.result_data
    ) {
      resultDataToProcess = metadata.normalized_result.result_data;
    }

    // Prioridad 3: Intentar desde frontMatter
    if (!resultDataToProcess && frontMatter.result_data) {
      resultDataToProcess = frontMatter.result_data;
    }
    if (!resultDataToProcess && frontMatter.rawResult) {
      resultDataToProcess = frontMatter.rawResult;
    }

    // Si no hay result_data en metadata, intentar extraerlo del contenido markdown
    if (!resultDataToProcess && parsed.content) {
      try {
        const trimmed = parsed.content.trim();
        const preguntaMatch = trimmed.match(
          /\*\*Pregunta:\*\*\s*([\s\S]*?)(?=\*\*Respuesta:\*\*|$)/
        );

        if (preguntaMatch) {
          const preguntaContent = preguntaMatch[1].trim();
          let preguntaJson: any = null;

          // Intentar parsear JSON de múltiples formas
          const jsonBlockMatch = preguntaContent.match(/```(?:json)?\s*\n([\s\S]*?)\n```/);
          if (jsonBlockMatch) {
            try {
              preguntaJson = JSON.parse(jsonBlockMatch[1].trim());
            } catch {
              // Continuar
            }
          }

          if (!preguntaJson) {
            const firstBrace = preguntaContent.indexOf('{');
            if (firstBrace !== -1) {
              let braceCount = 0;
              let jsonEnd = -1;

              for (let i = firstBrace; i < preguntaContent.length; i++) {
                if (preguntaContent[i] === '{') braceCount++;
                if (preguntaContent[i] === '}') {
                  braceCount--;
                  if (braceCount === 0) {
                    jsonEnd = i;
                    break;
                  }
                }
              }

              if (jsonEnd !== -1) {
                try {
                  preguntaJson = JSON.parse(preguntaContent.substring(firstBrace, jsonEnd + 1));
                } catch {
                  // Continuar
                }
              }
            }
          }

          if (!preguntaJson && preguntaContent.startsWith('{') && preguntaContent.endsWith('}')) {
            try {
              preguntaJson = JSON.parse(preguntaContent);
            } catch {
              // Continuar
            }
          }

          if (preguntaJson) {
            resultDataToProcess = preguntaJson;
          }
        }
      } catch {
        // Error extrayendo JSON de pregunta
      }
    }

    // Construir rawResult igual que StudiosPanel
    if (resultDataToProcess) {
      const rawResult = {
        assistant_message: {
          content: parsed.content,
          attachments: {
            studio_metadata: {
              result_data: resultDataToProcess,
            },
          },
          citations: frontMatter.citations || {},
          usage_stats: frontMatter.usage_stats,
          rag_stats: frontMatter.rag_stats,
          created_at: metadata.created_at || new Date().toISOString(),
        },
        metadata: {
          ...frontMatter,
          usage_stats: frontMatter.usage_stats,
          rag_stats: frontMatter.rag_stats,
        },
        usage_stats: frontMatter.usage_stats,
        rag_stats: frontMatter.rag_stats,
        cached: frontMatter.cached || false,
        generated_at: frontMatter.generated_at || metadata.created_at || new Date().toISOString(),
      };

      // Normalizar y extraer contenido
      const normalized = normalizeStudioResult(rawResult);

      if (normalized && normalized.result_data) {
        const extracted = extractContentText(normalized.result_data);

        if (extracted && extracted.trim().length > 0) {
          // Construir objeto result para renderizado
          return {
            ...normalized.result_data,
            citations: normalized.citations,
            content: extracted,
            originalContent: extracted,
          };
        }
      }

      // Fallback: usar result_data directamente
      if (normalized && normalized.result_data) {
        return {
          ...normalized.result_data,
          citations: normalized.citations,
          content: normalized.originalContent,
          originalContent: normalized.originalContent,
        };
      }
    }

    // Último fallback: intentar usar el contenido parseado directamente
    // Si el contenido parseado tiene estructura, intentar procesarlo igual que StudiosPanel
    if (!resultDataToProcess && parsed.content && parsed.content.trim().length > 0) {
      try {
        // Intentar parsear el contenido como JSON directamente
        const trimmed = parsed.content.trim();

        // Si el contenido empieza con {, intentar parsearlo como JSON
        if (trimmed.startsWith('{')) {
          try {
            const parsedJson = JSON.parse(trimmed);
            if (parsedJson && typeof parsedJson === 'object') {
              resultDataToProcess = parsedJson;
            }
          } catch {
            // Continuar
          }
        }

        // Si no, buscar JSON en el contenido
        if (!resultDataToProcess) {
          const firstBrace = trimmed.indexOf('{');
          if (firstBrace !== -1) {
            let braceCount = 0;
            let jsonEnd = -1;

            for (let i = firstBrace; i < trimmed.length; i++) {
              if (trimmed[i] === '{') braceCount++;
              if (trimmed[i] === '}') {
                braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i;
                  break;
                }
              }
            }

            if (jsonEnd !== -1) {
              try {
                const parsedJson = JSON.parse(trimmed.substring(firstBrace, jsonEnd + 1));
                if (parsedJson && typeof parsedJson === 'object') {
                  resultDataToProcess = parsedJson;
                }
              } catch {
                // Continuar
              }
            }
          }
        }

        // Si encontramos JSON, procesarlo
        if (resultDataToProcess) {
          const rawResult = {
            assistant_message: {
              content: parsed.content,
              attachments: {
                studio_metadata: {
                  result_data: resultDataToProcess,
                },
              },
              citations: frontMatter.citations || {},
              usage_stats: frontMatter.usage_stats,
              rag_stats: frontMatter.rag_stats,
              created_at: metadata.created_at || new Date().toISOString(),
            },
            metadata: {
              ...frontMatter,
              usage_stats: frontMatter.usage_stats,
              rag_stats: frontMatter.rag_stats,
            },
            usage_stats: frontMatter.usage_stats,
            rag_stats: frontMatter.rag_stats,
            cached: frontMatter.cached || false,
            generated_at:
              frontMatter.generated_at || metadata.created_at || new Date().toISOString(),
          };

          const normalized = normalizeStudioResult(rawResult);

          if (normalized && normalized.result_data) {
            const extracted = extractContentText(normalized.result_data);

            if (extracted && extracted.trim().length > 0) {
              return {
                ...normalized.result_data,
                citations: normalized.citations,
                content: extracted,
                originalContent: extracted,
              };
            }

            if (normalized && normalized.result_data) {
              return {
                ...normalized.result_data,
                citations: normalized.citations,
                content: normalized.originalContent,
                originalContent: normalized.originalContent,
              };
            }
          }
        }
      } catch {
        // Fallback processing failed
      }
    }

    return null;
  }, [viewerData]);

  // Renderizar el contenido del visor con el header unificado
  const renderViewerContent = () => {
    switch (viewerData.type) {
      case 'pdf':
        return (
          <PDFViewerPanel
            citationToView={{
              documentPath: viewerData.documentPath ?? '',
              documentName: viewerData.documentName ?? '',
              documentDisplayName: viewerData.documentDisplayName,
              document_id: viewerData.documentId,
              page: viewerData.page ?? 1,
              highlightText: viewerData.highlightText ?? '',
              chunkId: viewerData.chunkId,
              snippet_coordinates: viewerData.snippetCoordinates,
              metadata: viewerData.metadata,
            }}
            onCitationClick={onCitationClick}
            onChunkClick={onChunkClick}
            onClose={onClose}
            onSnippetSaved={onSnippetSaved}
            hideHeader={true}
          />
        );

      case 'snippet':
        const snippetData = mapViewerDataToPDFSnippet(viewerData);
        if (!snippetData) {
          return (
            <div
              className={`flex flex-col items-center justify-center h-full w-full ${theme.colors.bg.primary} text-red-500`}
            >
              <AlertCircle size={48} className="mb-4" />
              <p className="text-lg font-semibold">Error: Datos de snippet inválidos</p>
            </div>
          );
        }
        return (
          <SnippetViewerPanel snippetToView={snippetData} onClose={onClose} hideHeader={true} />
        );

      case 'chunk':
        const chunkData = mapViewerDataToChunk(viewerData);
        if (!chunkData) {
          return (
            <div
              className={`flex flex-col items-center justify-center h-full w-full ${theme.colors.bg.primary} text-red-500`}
            >
              <AlertCircle size={48} className="mb-4" />
              <p className="text-lg font-semibold">Error: Datos de chunk inválidos</p>
            </div>
          );
        }
        return <ChunksViewerPanel chunksToView={[chunkData]} onClose={onClose} hideHeader={true} />;

      case 'studio-result':
        // Renderizar resultado de Studio con MarkdownRenderer
        if (!studioResult) {
          return (
            <div
              className={`flex flex-col items-center justify-center h-full w-full ${theme.colors.bg.primary} text-red-500`}
            >
              <AlertCircle size={48} className="mb-4" />
              <p className="text-lg font-semibold">Error: No se pudo procesar el Studio</p>
              <p className="text-sm">El contenido guardado no tiene el formato esperado.</p>
            </div>
          );
        }

        // Extraer contenido para renderizar
        const studioContent = studioResult.content || studioResult.originalContent || '';
        const studioName =
          (viewerData.metadata?.studio_name as string) || viewerData.title || 'Studio';

        return (
          <div className="h-full overflow-y-auto scrollbar-thin p-4">
            <div className={`${theme.colors.text.primary}`}>
              <h2 className="text-xl font-semibold mb-4">{studioName}</h2>
              <div className={`prose prose-invert max-w-none ${theme.colors.text.secondary}`}>
                <MarkdownRenderer content={studioContent} />
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div
            className={`flex flex-col items-center justify-center h-full w-full ${theme.colors.bg.primary} text-red-500`}
          >
            <AlertCircle size={48} className="mb-4" />
            <p className="text-lg font-semibold">Error: Tipo de visor no reconocido</p>
            <p className="text-sm">No se puede mostrar el contenido.</p>
          </div>
        );
    }
  };

  return (
    <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
      <div className="flex-1 overflow-hidden">{renderViewerContent()}</div>
    </div>
  );
};

export default DocumentViewer;

// Re-export helper functions for backward compatibility
export {
  createPDFViewerData,
  createSnippetViewerData,
  createChunkViewerData,
} from '../utils/viewerHelpers';
